import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { Turbo } from "@hotwired/turbo-rails"
import { dragLiftOptions } from "../lib/drag_lift"

export default class extends Controller {
  static targets = ["handle"]
  static values = {
    url: String,
    group: String,
    draggable: String,
    eventId: Number
  }

  connect() {
    // This controller is primarily for reordering tables
    // Guest drag-and-drop is handled by table_card_controller

    // The tables list always drags by its handle, even when it connects empty
    // and the handles only arrive later via Turbo Streams.
    const isTablesList = this.element.id === 'tables-list'

    const options = {
      ...dragLiftOptions,
      handle: (this.hasHandleTarget || isTablesList) ? '.drag-handle' : null,
      chosenClass: 'bg-blue-50',

      onEnd: (evt) => {
        if (this.hasUrlValue) {
          this.updateOrder(evt)
        }
      }
    }

    // Set draggable selector based on what we're sorting
    if (this.hasDraggableValue) {
      options.draggable = this.draggableValue
    } else if (isTablesList) {
      // For table reordering. Tables only move within this list: they must
      // never be handed to (or accepted from) the guest/seat lists.
      options.draggable = '[data-table-id]'
      options.group = { name: 'tables', pull: false, put: false }
    }

    // Only add group options if explicitly needed
    if (this.hasGroupValue && this.groupValue === 'guests') {
      options.group = {
        name: this.groupValue,
        pull: true,
        put: ['guests']  // Accept seated guests and plus-ones being dropped back, never tables
      }
      options.draggable = '[data-controller~="guest-card"], [data-controller~="seated-guest"]'

      options.onAdd = (evt) => {
        const guestCard = evt.item
        const assignmentId = guestCard.dataset.seatedGuestAssignmentIdValue

        if (assignmentId) {
          this.unassignGuest(assignmentId, guestCard)
        }
      }
    }

    this.sortable = Sortable.create(this.element, options)
  }
  
  unassignGuest(assignmentId, guestCard) {
    const eventId = this.eventIdValue
    if (!eventId) {
      console.error('No event ID available for unassign')
      location.reload()
      return
    }

    const url = `/events/${eventId}/seat_assignments/${assignmentId}`
    const token = document.querySelector('[name="csrf-token"]').content

    fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': token,
        'Accept': 'text/vnd.turbo-stream.html'
      }
    }).then(response => {
      if (!response.ok) {
        console.error('Failed to unassign guest')
        location.reload()
      } else {
        return response.text()
      }
    }).then(html => {
      if (html) {
        Turbo.renderStreamMessage(html)
        document.dispatchEvent(new CustomEvent('guest:unassigned'))
      }
    }).catch(error => {
      console.error('Error unassigning guest:', error)
      location.reload()
    })
  }

  updateOrder(evt) {
    const ids = Array.from(this.element.children).map(item => {
      // Try different ways to get the ID
      return item.dataset.tableId || 
             item.dataset.guestId || 
             item.id?.replace(/^table_/, '') ||
             item.id?.replace(/^guest_/, '')
    }).filter(id => id)
    
    if (ids.length === 0) return
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    
    fetch(this.urlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        table_ids: ids,
        guest_ids: ids
      })
    }).then(response => {
      if (!response.ok) {
        // Show the order the server actually has rather than an unsaved one
        console.error('Failed to update order')
        location.reload()
      }
    }).catch(error => {
      console.error('Error updating order:', error)
      location.reload()
    })
  }
  
  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }
}